<template>
  <div
    v-if="!isEmpty"
    ref="rankingItems"
  >
    <InsightsRankingItem
      v-for="item in items"
      :key="item.id"
      :item="item"
      :type="type"
      :collapsed="collapsed"
      :max="relativeMaxPercentage"
      :widget-width="widgetWidth"
    >
      <template #ranking-item-name-postfix>
        <a
          v-if="isOwner || isManager"
          v-track:click="trackEditClick"
          :href="insightsSettingsUrl(item.name)"
          target="_blank"
          class="postfix postfix-hover"
        >
          <i class="hi hi-toggles" />
          Edit classification
        </a>
      </template>
    </InsightsRankingItem>
  </div>
  <div v-else-if="collapsed">No data available</div>
  <InsightsNoData
    v-else
    :type="emptyReason"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { get } from 'lodash-es';

import { mapGetters } from 'vuex';
import { applyTemplate } from '~/utils/url_template';
import { INSIGHTS_SETTINGS_URL } from '~/app_constants';
import InsightsRankingItem from './InsightsRankingItem.vue';
import { InsightsTracking } from '~/mixins';

export default {
  name: 'InsightRankingItems',
  components: {
    InsightsRankingItem,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [InsightsTracking],
  props: {
    type: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'medium',
    },
    widgetKey: {
      type: String,
      required: true,
    },
    widgetData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      widgetWidth: null,
    };
  },
  computed: {
    ...mapGetters(['organizationId', 'isOwner', 'isManager']),
    collapsed() {
      return this.size === 'small';
    },
    relativeMaxPercentage() {
      let max = 1;

      if (!this.isEmpty) {
        const base = this.items.reduce((prev, cur) => (prev.value > cur.value ? prev : cur));

        max = get(base, 'value', max);
      }

      return max;
    },
    isEmpty() {
      return this.widgetData && 'empty_reason' in this.widgetData;
    },
    emptyReason() {
      return this.widgetData?.empty_reason || 'no_data';
    },
    trackEditClick() {
      return Object.freeze({
        label: 'Widget click through',
        widget_name: this.label,
        link_type: 'edit classification',
        ...this.commonProps(),
      });
    },
  },
  mounted() {
    this.setupResizeObserver();
  },
  methods: {
    insightsSettingsUrl(itemName) {
      const url = applyTemplate(`${INSIGHTS_SETTINGS_URL}`, {
        organizationId: this.organizationId,
      });

      return url && itemName ? `${url}?search=${itemName}` : '#';
    },
    setupResizeObserver() {
      const el = this.$refs.rankingItems;

      if (!el) {
        return;
      }
      // Using Observer we can inform RankingItem component the width has changed
      // so that it can check if text overflows and show tooltip if needed.

      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach(() => {
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = setTimeout(() => {
            this.widgetWidth = el.clientWidth;
          }, 50);
        });
      });
      this.resizeObserver.observe(el);
    },
  },
};
</script>
